import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type {
  OrderDocument,
  OrderDocumentKind,
  PostalAddress,
} from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { ordersApi } from '../api/orders-api';
import { orderKeys } from '../hooks/order-keys';

const PRINT_ID = 'order-document';

/**
 * Only the document prints: everything else on the page is hidden for the printer, and the sheet
 * flows from the top of the first page.
 */
const PRINT_CSS = `@media print {
  body * { visibility: hidden !important; }
  #${PRINT_ID}, #${PRINT_ID} * { visibility: visible !important; }
  #${PRINT_ID} { position: absolute; inset: 0 auto auto 0; width: 100%; padding: 0; color: #000; background: #fff; }
  [data-print-hide] { display: none !important; }
}`;

function AddressBlock({ title, address }: { title: string; address: PostalAddress | null }) {
  if (!address) return null;
  const name = [address.firstName, address.lastName].filter(Boolean).join(' ');
  const city = [address.city, address.region, address.postalCode].filter(Boolean).join(', ');
  return (
    <div>
      <p className="text-xs font-semibold tracking-wide uppercase">{title}</p>
      <address className="text-sm not-italic">
        {[name, address.company, address.line1, address.line2, city, address.countryCode]
          .filter(Boolean)
          .map((line, index) => (
            <span key={index} className="block">
              {line}
            </span>
          ))}
        {address.phone ? <span className="block">{address.phone}</span> : null}
      </address>
    </div>
  );
}

function DocumentSheet({ doc }: { doc: OrderDocument }) {
  const { t } = useTranslation('orders');
  const format = useFormat();
  const storeAddress = doc.store.address;
  return (
    <article id={PRINT_ID} className="flex flex-col gap-6 text-sm">
      <header className="flex items-start justify-between gap-6">
        <div>
          {doc.store.logoUrl ? (
            <img src={doc.store.logoUrl} alt={doc.store.name} className="mb-2 h-10 w-auto" />
          ) : (
            <p className="text-lg font-semibold">{doc.store.name}</p>
          )}
          {doc.store.legalName ? <p>{doc.store.legalName}</p> : null}
          {storeAddress ? (
            <p className="text-muted-foreground">
              {[
                storeAddress.line1,
                storeAddress.line2,
                storeAddress.city,
                storeAddress.region,
                storeAddress.postalCode,
                storeAddress.countryCode,
              ]
                .filter(Boolean)
                .join(', ')}
            </p>
          ) : null}
          <p className="text-muted-foreground">
            {[doc.store.email, doc.store.phone, doc.store.domain].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xl font-semibold">{t(`documents.kind.${doc.kind}`)}</p>
          <p className="font-mono">{doc.order.name}</p>
          <p>{t('documents.placed', { when: format.date(doc.order.placedAt) })}</p>
          {doc.kind === 'invoice' && doc.order.paidAt ? (
            <p>{t('documents.paid', { when: format.date(doc.order.paidAt) })}</p>
          ) : null}
        </div>
      </header>
      <section className="grid gap-6 sm:grid-cols-3">
        <AddressBlock title={t('address.shipping')} address={doc.shippingAddress} />
        <AddressBlock title={t('address.billing')} address={doc.billingAddress} />
        <div>
          <p className="text-xs font-semibold tracking-wide uppercase">{t('documents.contact')}</p>
          {doc.order.email ? <p>{doc.order.email}</p> : null}
          {doc.order.phone ? <p>{doc.order.phone}</p> : null}
          {doc.shippingMethod ? (
            <p>{t('documents.shippingMethod', { method: doc.shippingMethod })}</p>
          ) : null}
          {doc.kind === 'invoice' && doc.order.paymentMethodName ? (
            <p>{t('documents.paymentMethod', { method: doc.order.paymentMethodName })}</p>
          ) : null}
        </div>
      </section>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b text-left text-xs uppercase">
            <th className="py-2">{t('lines.item')}</th>
            <th className="py-2">{t('lines.sku')}</th>
            <th className="py-2 text-right">{t('lines.quantity')}</th>
            {doc.showPrices ? (
              <>
                <th className="py-2 text-right">{t('lines.unitPrice')}</th>
                <th className="py-2 text-right">{t('lines.total')}</th>
              </>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {doc.lines.map((line) => (
            <tr key={line.id} className="border-b align-top">
              <td className="py-2">
                <p className="font-medium">{line.title}</p>
                {line.variantTitle ? <p>{line.variantTitle}</p> : null}
                {[
                  ...line.options.map((o) => `${o.name}: ${o.value}`),
                  ...line.customizations.map((c) => `${c.label}: ${c.value}`),
                ].map((text) => (
                  <p key={text} className="text-muted-foreground text-xs">
                    {text}
                  </p>
                ))}
              </td>
              <td className="py-2 font-mono text-xs">{line.sku ?? '—'}</td>
              <td className="py-2 text-right tabular-nums">{line.quantity}</td>
              {doc.showPrices ? (
                <>
                  <td className="py-2 text-right tabular-nums">
                    {line.unitPrice ? format.money(line.unitPrice) : '—'}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {line.total ? format.money(line.total) : '—'}
                  </td>
                </>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      {doc.showPrices && doc.totals ? (
        <dl className="ml-auto grid w-64 grid-cols-2 gap-y-1 tabular-nums">
          {(
            [
              'subtotal',
              'discount',
              'shipping',
              'tax',
              'total',
              'paid',
              'refunded',
              'outstanding',
            ] as const
          )
            .filter(
              (key) => key === 'total' || key === 'subtotal' || doc.totals?.[key].amount !== 0,
            )
            .map((key) => (
              <div key={key} className="contents">
                <dt className={key === 'total' ? 'font-semibold' : undefined}>
                  {t(`totals.${key}`)}
                </dt>
                <dd className={`text-right ${key === 'total' ? 'font-semibold' : ''}`}>
                  {doc.totals ? format.money(doc.totals[key]) : null}
                </dd>
              </div>
            ))}
        </dl>
      ) : null}
      {doc.order.note ? (
        <section>
          <p className="text-xs font-semibold tracking-wide uppercase">
            {t('detail.customerNote')}
          </p>
          <p className="whitespace-pre-line">{doc.order.note}</p>
        </section>
      ) : null}
      {doc.policies.length > 0 ? (
        <ul className="text-muted-foreground list-disc pl-5 text-xs">
          {doc.policies.map((policy) => (
            <li key={policy}>{policy}</li>
          ))}
        </ul>
      ) : null}
      {doc.footer ? (
        <footer className="border-t pt-3 text-xs whitespace-pre-line">{doc.footer}</footer>
      ) : null}
    </article>
  );
}

export interface OrderDocumentDialogProps {
  storeId: string;
  orderId: string;
  kind: OrderDocumentKind;
  onOpenChange: (open: boolean) => void;
}

/**
 * The packing slip or invoice as the backend models it, laid out for A4/Letter and printed with
 * the browser. The packing slip hides prices when the store's document settings say so.
 */
export function OrderDocumentDialog({
  storeId,
  orderId,
  kind,
  onOpenChange,
}: OrderDocumentDialogProps) {
  const { t } = useTranslation('orders');
  const query = useQuery({
    queryKey: [...orderKeys.detail(storeId, orderId), 'document', kind],
    queryFn: () => ordersApi.document(storeId, orderId, kind),
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-4xl">
        <style>{PRINT_CSS}</style>
        <DialogHeader data-print-hide>
          <DialogTitle>{t(`documents.kind.${kind}`)}</DialogTitle>
          <DialogDescription>{t('documents.description')}</DialogDescription>
        </DialogHeader>
        <div data-print-hide className="flex justify-end">
          <Button type="button" disabled={!query.data} onClick={() => window.print()}>
            <Printer aria-hidden />
            {t('documents.print')}
          </Button>
        </div>
        {query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : query.data ? (
          <DocumentSheet doc={query.data} />
        ) : (
          <Skeleton className="h-96 w-full" />
        )}
      </DialogContent>
    </Dialog>
  );
}
